<script setup>
import { onBeforeMount, ref, useTemplateRef } from 'vue'
import { useRoute } from 'vue-router'
import { getUserByReferenceAndVr } from '@/services/userService.js'

import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import ButtonSalva from '@/components/Generics/Buttons/ButtonSalva.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFileImport } from '@digitalpa/pro-solid-svg-icons'
import TranslationProvider from '@/provider/translations.js'

library.add(faFileImport)

const activeTab = ref('stati')
const workflow = ref([])

const permissions = ref()
const users = ref([])
const isLoading = ref(true)
const isUploading = ref(false)

const isLoadingWorkflow = ref(true)
const state = ref([])
const transition = ref([])

const route = useRoute()
const routeId = route.params.id
const routeType = route.params.type
const props = defineProps(['updatePageTitle'])

const modalImportPermissionsFromCampaign = useTemplateRef('modal-import-permissions-from-campaign')
const isLoadingImportFromCampaign = ref(false)

function usersWithUsernames(item) {

  if (!item || !users.value[item.name]) {
    return
  }

  users.value[item.name].map(user => {

    let displayName = user.username
    if (Array.isArray(user.personRelations) && user.personRelations.length > 0) {
      const person = user.personRelations[0]
      if (person.person.firstName && person.person.lastName) {
        displayName = `${person.person.firstName} ${person.person.lastName}`
      }
    }

    user.username = displayName

  })

  return users.value[item.name]
}

onBeforeMount(async () => {
  let pageTitle = 'Gestione Permessi ' + (routeType === 'campaign' ? 'Campagna' : 'Compilazione') + ' - ' + routeId
  props.updatePageTitle(pageTitle)

  await getData()
  await getStateAndTransitions()
  await getInitialSelectedUsers(state.value, transition.value)

  isLoading.value = false
})

const getData = async () => {
  try {
    let response = null
    if (routeType === 'campaign') {
      let url = `/campaign/api/campaign/${routeId}`
      response = await client.get(url)
    } else if (routeType === 'compiled-poll') {
      let url = `/compiled-poll/api/compiled-poll/${routeId}?poll=1`
      response = await client.get(url)
      response = response.data
    }
    if (!response) {
      return
    }

    state.value = response.permissions.places
    transition.value = response.permissions.transitions

    if (routeType === 'campaign') {
      workflow.value = response
    } else if (routeType === 'compiled-poll') {
      let url = `/campaign/api/campaign/${response.campaign}`
      response = await client.get(url)
      workflow.value = response

    }
  } catch (error) {
    toast.create(TranslationProvider.getTrans('app.network_error'), 'error')
  }
}

const getStateAndTransitions = async () => {
  try {
    let url = `/campaign/api/workflow/${workflow.value.workflow}`
    const response = await client.get(url)

    state.value = state.value.map(stateItem => {
      const matchingPlace = response.data.places.find(place => place.name === stateItem.name)
      return {
        ...stateItem,
        label: matchingPlace ? matchingPlace.labels.it : stateItem.name
      }
    })

    transition.value = transition.value.map(transitionItem => {
      const matchingTransition = response.data.transitions.find(trans => trans.name === transitionItem.name)
      return {
        ...transitionItem,
        label: matchingTransition ? matchingTransition.labels.it : transitionItem.name
      }
    })

    isLoadingWorkflow.value = false
  } catch (error) {
  }
}

const getUsers = async (fullSearch = null, item = null) => {
  if (!item || (fullSearch !== null && fullSearch.length < 3)) {
    return
  }

  try {
    let url = `/custom/cmb/search-users?fullSearch=${fullSearch}&sortBy=username&direction=asc`
    const response = await client.get(url)
    let references = item.edit?.userReferences ?? item.can?.userReferences ?? []

    for (const ref of references) {
      let exists = response.data.some(user => user?.reference === ref)
      if (!exists) {
        let user = await getUserByReferenceAndVr(ref, 'cmb')
        response.data.push(user)
      }
    }

    users.value[item.name] = response.data
  } catch (error) {
  }
}

async function getInitialSelectedUsers(places, transitions) {

  for (const place of places) {
    let userReferences = place.edit.userReferences
    if (!userReferences) continue
    await getSelectedUser(userReferences, place)
  }

  for (const transition of transitions) {
    let userReferences = transition.can.userReferences
    if (!userReferences) continue
    await getSelectedUser(userReferences, transition)
  }
}

async function getSelectedUser(userReferences, state) {
  for (const reference of userReferences) {
    let user = await getUserByReferenceAndVr(reference, 'cmb')

    if (!users.value[state.name]) {
      users.value[state.name] = []
    }

    users.value[state.name].push(user)
  }
}

const submitForm = async () => {
  try {
    isUploading.value = true
    const statePayload = state.value.map(({ label, ...rest }) => rest)
    const transitionPayload = transition.value.map(({ label, ...rest }) => rest)

    const payload = {

      places: statePayload,
      transitions: transitionPayload

    }

    let url = `/campaign/api/campaign/${routeId}/permissions`
    if (routeType === 'compiled-poll') {
      url = `/compiled-poll/api/compiled-poll/${routeId}/permissions`
    }
    workflow.value = await client.post(url, payload, 'application/json')
    toast.create('Permessi salvati con successo!', 'success')
  } catch (error) {
    isUploading.value = false
    toast.create('Errore nel salvataggio dei dati , riprova più tardi', 'error')
  } finally {
    isUploading.value = false
  }
}

const handleModalImportPermissionsFromCampaign = (event, close = false) => {
  if (close) {
    modalImportPermissionsFromCampaign.value.classList.remove('d-block')
  } else {
    modalImportPermissionsFromCampaign.value.classList.add('d-block')
  }
}

const importPermissionsFromCampaign = async () => {
  isLoadingImportFromCampaign.value = true
  try {
    state.value = workflow.value.permissions.places
    transition.value = workflow.value.permissions.transitions

    await submitForm()
    isLoading.value = true
    await getData()
    await getStateAndTransitions()
    await getUsers()
    isLoading.value = false
    isLoadingImportFromCampaign.value = false
  } catch (e) {
    console.error(e)
    toast.create('Errore nel salvataggio dei dati , riprova più tardi', 'error')
  }
  handleModalImportPermissionsFromCampaign(null, true)
}
</script>

<template>
  <div class="container-fluid px-0">
    <div class="card shadow-none">
      <div class="card-body p-0">
        <div class="my-4 d-flex ps-2" v-if="routeType === 'compiled-poll'">
          <button type="button" class="btn btn-primary"
                  title="Importa permessi dalla campagna" @click="handleModalImportPermissionsFromCampaign">
            <font-awesome-icon icon="fa-file-import" class="pe-2" />
            Importa dalla campagna
          </button>
        </div>
        <!-- Tab Navigation -->
        <ul class="nav nav-tabs mt-3 px-2" id="formTabs" role="tablist">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'stati' }"
              @click="activeTab = 'stati'"
              href="javascript:void(0)"
            >
              Stati
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'transizioni' }"
              @click="activeTab = 'transizioni'"
              href="javascript:void(0)"
            >
              Transizioni
            </a>
          </li>
        </ul>

        <loading v-if="isLoading"></loading>

        <div v-else>
          <div v-if="activeTab === 'stati'">
            <div v-if="state && state.length > 0">
              <div v-for="(item, index) in state" :key="index" class="my-2 p-2">
                <h2>{{ item.label }}</h2>
                <div>
                  <label for="users">Utenti</label>
                  <v-select
                    multiple
                    :clearSearchOnBlur="() => true"
                    @search="fullSearch => getUsers(fullSearch, item)"
                    :options="usersWithUsernames(item)"
                    :reduce="user => user.reference || user"
                    label="username"
                    v-model="item.edit.userReferences"
                  >
                    <template #no-options>
                      <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                    </template>
                  </v-select>
                  <div class="form-text">
                    Inserisci almeno 3 caratteri per iniziare la ricerca
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="!isLoadingWorkflow">
              <div class="alert alert-warning mt-2 mx-2" role="alert">
                Nessuno stato disponibile.
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'transizioni'">
            <div v-if="transition && transition.length > 0">
              <div v-for="(item, index) in transition" :key="index" class="my-2 p-2">
                <h2>{{ item.label }}</h2>
                <div class="form-group">
                  <label for="users">Utenti</label>
                  <v-select
                    multiple
                    :clearSearchOnBlur="() => true"
                    @search="fullSearch => getUsers(fullSearch, item)"
                    :options="usersWithUsernames(item)"
                    :reduce="user => user.reference || user"
                    label="username"
                    v-model="item.can.userReferences"
                  >
                    <template #no-options>
                      <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                    </template>
                  </v-select>
                  <div class="form-text">
                    Inserisci almeno 3 caratteri per iniziare la ricerca
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="!isLoadingWorkflow">
              <div class="alert alert-warning mt-2 mx2" role="alert">
                Nessuna transizione disponibile.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <button-torna-alla-lista
        @click="$router.back"
        class="me-auto">
      </button-torna-alla-lista>
      <button-salva
        @click="submitForm"
        :disabled="isUploading"
        :loading="isUploading">
      </button-salva>
    </div>

  </div>
  <div v-if="routeType === 'compiled-poll'" class="modal" tabindex="-1" ref="modal-import-permissions-from-campaign"
       style="background-color: rgba(0,0,0,0.8);">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Importazione permessi e ruoli dalla campagna</h5>
          <button v-if="!isLoadingImportFromCampaign" type="button" class="btn-close" data-bs-dismiss="modal"
                  aria-label="Close" @click="handleModalImportPermissionsFromCampaign($event, true)"></button>
        </div>
        <div class="modal-body">
          <p>I permessi ed i ruoli verranno importati dalla relativa campagna e salvati</p>
          <p>Si vuole procedere?</p>
        </div>
        <div class="modal-footer">
          <template v-if="!isLoadingImportFromCampaign">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"
                    @click="handleModalImportPermissionsFromCampaign($event, true)">Annulla
            </button>
            <button type="button" class="btn btn-primary" @click="importPermissionsFromCampaign">Importa</button>
          </template>
          <template v-else>
            <button type="button" class="btn btn-primary" disabled>
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              <span class="visually-hidden" role="status">Loading...</span>
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

:root,
:host {
  @extend :root;
}

.btn-sm [class*='fa-'] {
  width: 0.9rem;
}

.btn:not(.btn-sm) [class*='fa-'] {
  width: 1.5rem;
}
</style>
